import { captureException } from '@sentry/react'
import { getAiImagesByIds_API } from 'api'
import { ErrorKeys } from 'consts/errorKeys'
import slice from 'store/features/aiAvatar/slice'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import {
  getPendingSourceImageEntry,
  removePendingSourceImageEntry,
  removeAssetOp,
  PENDING_SOURCE_IMAGE_TTL_MS
} from 'store/features/aiAvatar/sourceImageStorage'
import { getSourceImageProgressId } from 'store/features/aiAvatar/utils'
import { projectSelectors } from 'store/features/project/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { generationProgressService } from 'services/generationProgress'
import { handleApiError } from 'store/utils'
import { TAppDispatch, TGetState, TRootState } from 'store/types'
import { IAvatarData } from 'store/types/aiAvatar'
import { withAppThunks } from 'store/with-app-thunks'
import { ApiError, ReduxThunkError } from 'utils/errors'

const { actions } = slice

// Floor for the reconcile's expiry backstop: even when the op is close to its
// TTL, give the socket at least this long after (re)arming before declaring
// the op dead — the reconcile fetch has just confirmed nothing is ready yet.
const MIN_EXPIRY_BACKSTOP_MS = 30 * 1000

// The `assetId` of the clip the open regen form targets (the case-2 ops-map
// key), or null for a fresh form / a target that no longer exists.
export const resolveTargetAssetId = (state: TRootState): string | null => {
  const targetMediaItemId = aiAvatarSelectors.targetMediaItemId(state)
  if (!targetMediaItemId) return null
  const targetItem = projectDataSelectors
    .mediaItems(state)
    .find(item => item.id === targetMediaItemId)
  return targetItem?.assetId ?? null
}

// Storage-side terminals for a reconciled op — the only part that differs
// between case 1 (single slot) and case 2 (assetId-keyed map).
interface IReconcileTerminals {
  // The op finished and was applied to the open form.
  onApplied: (result: { url: string; width: number; height: number }) => void
  // Nothing arrived by the TTL — the op is considered dead.
  onExpired: () => void
}

// Shared RECONCILE for an op that is armed in the form's slot
// (`pendingSourceImageId`), used after a page reload by both the case-1
// restore below and the case-2 regen-form re-arm (`setRegenerationForm`).
// Missed `type:'ai-image'` events are never replayed (INV-AIMG-1), so if the
// generation finished while the page was gone the loader would hang forever —
// ask `user-images-by-ids` (works for Resize's recommendation-api ids too —
// verified): finished → apply to the form now; anything else → still
// generating, keep the loader and let the live socket complete it. NOTE: the
// endpoint returns ONLY finished projects — an in-progress op comes back
// EMPTY (verified live with a mid-resize reload), so "not found" is NOT a
// failure signal and must never surface an error. The failure ceiling is the
// TTL backstop instead: it self-cancels whenever the slot no longer holds
// this op (socket completed it, or the user replaced/abandoned the avatar).
export const reconcilePendingSourceImage = (
  op: { imageId: number; targetAspectRatio: string | null; createdAt: number },
  terminals: IReconcileTerminals
) =>
  withAppThunks(
    appThunks =>
      async (dispatch: TAppDispatch, getState: TGetState): Promise<void> => {
        try {
          const { data } = await getAiImagesByIds_API({ ids: [op.imageId] })

          // The socket may have completed (or the user replaced the avatar)
          // while the fetch was in flight — the correlation is gone then, and
          // this stale response must not double-apply.
          const isStillPending =
            aiAvatarSelectors.pendingSourceImageId(getState()) === op.imageId
          if (!isStillPending) return

          // Same completion gate as the socket path (INV-AIMG-2): only apply a
          // fully-resolved frame.
          const project = data[0]
          const filePath = project?.images?.[0]?.filePath
          if (filePath && project.width && project.height) {
            dispatch(
              actions.applySourceImageResult({
                url: filePath,
                width: project.width,
                height: project.height,
                aspectRatio: op.targetAspectRatio
              })
            )
            terminals.onApplied({
              url: filePath,
              width: project.width,
              height: project.height
            })
            // Progress terminal — mirrors the socket path's complete().
            generationProgressService.complete({
              mediaItemId: getSourceImageProgressId(op.imageId)
            })
            dispatch(appThunks.creditsThunks.getUserCredits())
            return
          }

          // Backstop for an op that died while we were away (its error socket
          // event is gone and the fetch can't distinguish "generating" from
          // "dead"): if nothing has arrived by the op's TTL, unwind instead of
          // spinning forever. A healthy resize/enhance finishes in a couple of
          // minutes, so at the 15-minute ceiling the op is genuinely dead.
          const remainingTtlMs = Math.max(
            op.createdAt + PENDING_SOURCE_IMAGE_TTL_MS - Date.now(),
            MIN_EXPIRY_BACKSTOP_MS
          )
          setTimeout(() => {
            const isSamePending =
              aiAvatarSelectors.pendingSourceImageId(getState()) === op.imageId
            if (!isSamePending) return
            dispatch(actions.setAvatarLoading(false))
            dispatch(actions.clearPendingSourceImage())
            terminals.onExpired()
            // Progress terminal — the op is dead, kill its ticker + storage.
            generationProgressService.stop({
              mediaItemId: getSourceImageProgressId(op.imageId),
              shouldRemoveFromStorage: true
            })
            dispatch(handleApiError(ErrorKeys.AI_IMAGE_GENERATION_FAILED))
          }, remainingTtlMs)
        } catch (error) {
          // Reconcile is best-effort: the socket remains the primary completion
          // channel, so keep the armed pending state (a later reload retries).
          if (!(error instanceof ApiError)) {
            captureException(new ReduxThunkError(error))
          }
        }
      }
  )

// Restores an in-flight case-1 Enhance/Resize after a page reload, from the
// localStorage single-slot entry written at kickoff. Runs on AI Avatar tab
// mount; a no-op when there is nothing to restore. SEED — validate the entry
// (right project, within TTL, form still fresh and empty) and reseed the form
// (kickoff avatar back under the loader, correlation re-armed for the socket)
// — then run the shared reconcile above.
const restorePendingSourceImage = () =>
  withAppThunks(
    () =>
      async (dispatch: TAppDispatch, getState: TGetState): Promise<void> => {
        const entry = getPendingSourceImageEntry()
        if (!entry) return

        const state = getState()
        const projectId = Number(projectSelectors.id(state))
        // Project not loaded yet — can't tell whose entry this is; leave it for
        // the next tab open instead of wrongly discarding it.
        if (!projectId) return

        if (
          projectId !== entry.videoProjectId ||
          Date.now() - entry.createdAt > PENDING_SOURCE_IMAGE_TTL_MS
        ) {
          removePendingSourceImageEntry()
          return
        }

        // Only a fresh, untouched form may be reseeded — never stomp an avatar
        // the user picked this session, an armed regen form, or an op already
        // in flight.
        const params = aiAvatarSelectors.generationParams(state)
        if (
          params.avatar ||
          params.targetMediaItemId ||
          aiAvatarSelectors.pendingSourceImageId(state) !== null
        ) {
          return
        }

        dispatch(
          actions.restorePendingSourceImage({
            avatar: entry.avatar,
            ratioOverride: entry.ratioOverride,
            id: entry.imageId,
            aspectRatio: entry.targetAspectRatio
          })
        )

        await dispatch(
          reconcilePendingSourceImage(
            {
              imageId: entry.imageId,
              targetAspectRatio: entry.targetAspectRatio,
              createdAt: entry.createdAt
            },
            {
              onApplied: () => removePendingSourceImageEntry(),
              onExpired: () => removePendingSourceImageEntry()
            }
          )
        )
      }
  )

// One-step undo with the case-2 write-back: restoring the previous image must
// also drop the clip's stored result, or the next reselect would rebuild the
// form from the map and resurrect the image the user just undid. Shadows the
// raw slice action (spread after `...actions` in `aiAvatarThunks`); for a
// fresh form (no target clip) it degrades to the raw action.
const undoSourceImageChange =
  () => (dispatch: TAppDispatch, getState: TGetState) => {
    const assetId = resolveTargetAssetId(getState())
    if (assetId) {
      removeAssetOp(assetId)
    }
    dispatch(actions.undoSourceImageChange())
  }

// Shadows of the raw slice actions (spread after `...actions` in
// `aiAvatarThunks`, the `resetGenerationParams` precedent): the reducers
// already abandon an in-flight op when the avatar is replaced or deleted, and
// the persisted case-1 entry must die at the same moments — otherwise a reload
// resurrects an op the user deliberately walked away from. (The case-2 map is
// intentionally NOT cleared here: a regen clip's op/result outlives the form.)
const setAvatar = (avatar: IAvatarData | null) => (dispatch: TAppDispatch) => {
  removePendingSourceImageEntry()
  dispatch(actions.setAvatar(avatar))
}

const clearAvatar = () => (dispatch: TAppDispatch) => {
  removePendingSourceImageEntry()
  dispatch(actions.clearAvatar())
}

export const sourceImageThunks = {
  restorePendingSourceImage,
  undoSourceImageChange,
  setAvatar,
  clearAvatar
}
